<template>
    <div class="generation-progress" :class="stateClasses">
        <svg class="generation-visual" viewBox="-40 0 392 112" fill="none" aria-hidden="true">
            <g class="generation-list">
                <g class="generation-mark generation-mark--first" transform="translate(26 92)">
                    <g class="generation-bullet">
                        <g class="generation-spin">
                            <g class="generation-lift">
                                <path class="generation-squash generation-squash--strong" d="M-26 0A26 26 0 0 1 26 0Z" fill="var(--generation-teal-strong)" />
                            </g>
                            <g class="generation-roll">
                                <g class="generation-fold">
                                    <g class="generation-drop">
                                        <path class="generation-squash generation-squash--base" d="M-26 0A26 26 0 0 1 26 0Z" fill="var(--generation-teal-base)" />
                                    </g>
                                </g>
                            </g>
                        </g>
                    </g>
                </g>
                <g class="generation-mark generation-mark--second" transform="translate(130 92)">
                    <g class="generation-bullet">
                        <g class="generation-spin">
                            <g class="generation-lift">
                                <path class="generation-squash generation-squash--strong" d="M-26 0A26 26 0 0 1 26 0Z" fill="var(--generation-teal-strong)" />
                            </g>
                            <g class="generation-roll">
                                <g class="generation-fold">
                                    <g class="generation-drop">
                                        <path class="generation-squash generation-squash--base" d="M-26 0A26 26 0 0 1 26 0Z" fill="var(--generation-teal-base)" />
                                    </g>
                                </g>
                            </g>
                        </g>
                    </g>
                </g>
                <g class="generation-mark generation-mark--third" transform="translate(234 92)">
                    <g class="generation-bullet">
                        <g class="generation-spin">
                            <g class="generation-lift">
                                <path class="generation-squash generation-squash--strong" d="M-26 0A26 26 0 0 1 26 0Z" fill="var(--generation-teal-strong)" />
                            </g>
                            <g class="generation-roll">
                                <g class="generation-fold">
                                    <g class="generation-drop">
                                        <path class="generation-squash generation-squash--base" d="M-26 0A26 26 0 0 1 26 0Z" fill="var(--generation-teal-base)" />
                                    </g>
                                </g>
                            </g>
                        </g>
                    </g>
                </g>
                <g transform="translate(0 92)">
                    <g class="generation-ball-x">
                        <g class="generation-ball-y">
                            <circle class="generation-ball" r="9" />
                        </g>
                    </g>
                </g>
                <rect class="generation-line generation-line--first" x="86" y="26" width="164" height="8" rx="4" fill="var(--color-border-strong)" />
                <rect class="generation-line generation-line--second" x="86" y="62" width="140" height="8" rx="4" fill="var(--color-border-strong)" />
                <rect class="generation-line generation-line--third" x="86" y="98" width="152" height="8" rx="4" fill="var(--color-border-strong)" />
            </g>
        </svg>
        <h3 data-testid="initial-generation-stage" class="generation-title text-ink" aria-live="polite" aria-atomic="true">{{ currentStage.title }}</h3>
        <p v-if="currentStage.messages.length" class="generation-copy scaffold-body text-ink-muted">
            <span :key="subtitleKey" data-testid="initial-generation-message" class="generation-copy-line" :class="currentStage.messages.length > 1 ? 'is-cycling' : 'is-holding'">
                {{ currentSubtitle }}
            </span>
        </p>
    </div>
</template>

<script setup lang="ts">
/* eslint design-system/no-off-scale-spacing: "off" -- This loading animation preserves authored scene framing and responsive artwork-to-caption spacing. */
import type { DevelopmentActionGenerationProgressStage } from "~vue/api/generated/development-journeys";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
    generationStage: DevelopmentActionGenerationProgressStage;
}>();

const emit = defineEmits<{
    ready: [];
}>();

const FIRST_TIMELINE_END_SECONDS = 7.8;
const ACTION_CREATION_TIMELINE_END_SECONDS = 5.6;
const IDLE_ROCK_DURATION_MS = 3_600;
const MINIMUM_STAGE_DISPLAY_DURATION_MS = 10_000;
const READY_DISPLAY_DURATION_MS = MINIMUM_STAGE_DISPLAY_DURATION_MS;
const SEARCH_SPIN_DURATION_MS = 1_600;
const SUBTITLE_CYCLE_DURATION_MS = 2_400;

const stageOrder = [
    "Getting started",
    "Learning about your work",
    "Finding the best opportunities",
    "Creating your actions",
    "Ready",
] as const satisfies readonly DevelopmentActionGenerationProgressStage[];

const stageValues: Record<DevelopmentActionGenerationProgressStage, string> = {
    "Getting started": "getting_started",
    "Learning about your work": "learning_about_your_work",
    "Finding the best opportunities": "finding_the_best_opportunities",
    "Creating your actions": "creating_your_actions",
    Ready: "ready",
};

// Locale catalogue can finish loading after mount, so stage copy must stay reactive to the active locale.
const stages = computed(
    () =>
        ({
            "Getting started": {
                title: t("developmentJourneys.initialGenerationProgress.stages.gettingStarted.title"),
                messages: [
                    t("developmentJourneys.initialGenerationProgress.stages.gettingStarted.message1"),
                    t("developmentJourneys.initialGenerationProgress.stages.gettingStarted.message2"),
                    t("developmentJourneys.initialGenerationProgress.stages.gettingStarted.message3"),
                ],
            },
            "Learning about your work": {
                title: t("developmentJourneys.initialGenerationProgress.stages.learningAboutYourWork.title"),
                messages: [
                    t("developmentJourneys.initialGenerationProgress.stages.learningAboutYourWork.message1"),
                    t("developmentJourneys.initialGenerationProgress.stages.learningAboutYourWork.message2"),
                ],
            },
            "Finding the best opportunities": {
                title: t("developmentJourneys.initialGenerationProgress.stages.findingTheBestOpportunities.title"),
                messages: [
                    t("developmentJourneys.initialGenerationProgress.stages.findingTheBestOpportunities.message1"),
                    t("developmentJourneys.initialGenerationProgress.stages.findingTheBestOpportunities.message2"),
                    t("developmentJourneys.initialGenerationProgress.stages.findingTheBestOpportunities.message3"),
                    t("developmentJourneys.initialGenerationProgress.stages.findingTheBestOpportunities.message4"),
                ],
            },
            "Creating your actions": {
                title: t("developmentJourneys.initialGenerationProgress.stages.creatingYourActions.title"),
                messages: [
                    t("developmentJourneys.initialGenerationProgress.stages.creatingYourActions.message1"),
                    t("developmentJourneys.initialGenerationProgress.stages.creatingYourActions.message2"),
                ],
            },
            Ready: {
                title: t("developmentJourneys.initialGenerationProgress.stages.ready.title"),
                messages: [],
            },
        }) satisfies Record<DevelopmentActionGenerationProgressStage, { title: string; messages: readonly string[] }>,
);

type AnimationState = "idle" | "first-timeline" | "searching" | "second-timeline" | "creating-hold" | "ready";

const animationState = ref<AnimationState>("idle");
const displayedStage = ref<DevelopmentActionGenerationProgressStage>(props.generationStage);
const subtitleIndex = ref(0);
const subtitleKey = ref(0);
const currentStage = computed(() => stages.value[displayedStage.value]);
const currentSubtitle = computed(() => currentStage.value.messages[subtitleIndex.value] ?? "");
const stateClasses = computed(() => ({
    "is-idle": animationState.value === "idle",
    "is-running": animationState.value !== "idle",
    "is-first-timeline": animationState.value === "first-timeline",
    "is-searching": animationState.value === "searching",
    "is-completing": ["second-timeline", "creating-hold", "ready"].includes(animationState.value),
    "is-second-timeline": animationState.value === "second-timeline",
    "is-creating-hold": animationState.value === "creating-hold",
    "is-ready": animationState.value === "ready",
    "is-creating-actions": displayedStage.value === "Creating your actions",
}));

let phaseTimers: ReturnType<typeof setTimeout>[] = [];
let stageMinimumTimer: ReturnType<typeof setTimeout> | undefined;
let readyTimer: ReturnType<typeof setTimeout> | undefined;
let subtitleTimer: ReturnType<typeof setInterval> | undefined;
let hasMetMinimumStageDuration = false;
let hasEmittedReady = false;
let hasFinishedActionCreationTimeline = false;
let hasFinishedInitialTimeline = false;
let idleStartedAt: number | undefined;
let searchStartedAt: number | undefined;
let visibleStageStartedAt: number | undefined;

const closeVisibleStage = (reason: "stage_changed" | "page_hidden" | "loader_closed", stage = displayedStage.value) => {
    if (visibleStageStartedAt === undefined) {
        return;
    }
    const durationMs = Math.max(0, Date.now() - visibleStageStartedAt);
    visibleStageStartedAt = undefined;
    void logUserInteraction(AmplitudeEventNames.DEVELOPMENT_JOURNEYS_GENERATION_STAGE_CLOSED, {
        development_journeys_generation_stage: stageValues[stage],
        development_journeys_stage_visible_ms: durationMs,
        development_journeys_stage_end_reason: reason,
    });
};

const viewVisibleStage = () => {
    if (globalThis.document.hidden || visibleStageStartedAt !== undefined) {
        return;
    }
    visibleStageStartedAt = Date.now();
    void logUserInteraction(AmplitudeEventNames.DEVELOPMENT_JOURNEYS_GENERATION_STAGE_VIEWED, {
        development_journeys_generation_stage: stageValues[displayedStage.value],
    });
};

const onVisibilityChange = () => {
    if (globalThis.document.hidden) {
        closeVisibleStage("page_hidden");
    } else {
        viewVisibleStage();
    }
};

const onPageHide = () => closeVisibleStage("page_hidden");

const clearPhaseTimers = () => {
    phaseTimers.forEach((timer) => globalThis.clearTimeout(timer));
    phaseTimers = [];
};

const clearTimers = () => {
    clearPhaseTimers();
    if (stageMinimumTimer !== undefined) {
        globalThis.clearTimeout(stageMinimumTimer);
        stageMinimumTimer = undefined;
    }
    if (readyTimer !== undefined) {
        globalThis.clearTimeout(readyTimer);
        readyTimer = undefined;
    }
    if (subtitleTimer !== undefined) {
        globalThis.clearInterval(subtitleTimer);
        subtitleTimer = undefined;
    }
};

const schedulePhase = (callback: () => void, duration: number) => {
    phaseTimers.push(globalThis.setTimeout(callback, duration));
};

const durationUntilLoopBoundary = (startedAt: number, loopDuration: number) => {
    const elapsed = Date.now() - startedAt;
    return (loopDuration - (elapsed % loopDuration)) % loopDuration;
};

const showIdleVisual = () => {
    clearPhaseTimers();
    animationState.value = "idle";
    hasFinishedActionCreationTimeline = false;
    hasFinishedInitialTimeline = false;
    idleStartedAt = Date.now();
    searchStartedAt = undefined;
};

const finishFirstTimeline = () => {
    showSearchVisual();
    hasFinishedInitialTimeline = true;
    advanceIfServerHasReachedNextStage();
};

const showFirstTimelineVisual = () => {
    clearPhaseTimers();
    hasFinishedInitialTimeline = false;
    const startFirstTimeline = () => {
        animationState.value = "first-timeline";
        idleStartedAt = undefined;
        schedulePhase(finishFirstTimeline, FIRST_TIMELINE_END_SECONDS * 1_000);
    };

    if (animationState.value === "idle" && idleStartedAt !== undefined) {
        schedulePhase(startFirstTimeline, durationUntilLoopBoundary(idleStartedAt, IDLE_ROCK_DURATION_MS));
        return;
    }
    startFirstTimeline();
};

const showSearchVisual = () => {
    if (animationState.value === "searching") {
        return;
    }
    clearPhaseTimers();
    animationState.value = "searching";
    idleStartedAt = undefined;
    searchStartedAt = Date.now();
};

const finishActionCreationTimeline = () => {
    clearPhaseTimers();
    animationState.value = "creating-hold";
    hasFinishedActionCreationTimeline = true;
    advanceIfServerHasReachedNextStage();
};

const showActionCreationVisual = () => {
    clearPhaseTimers();
    hasFinishedActionCreationTimeline = false;
    const startActionCreationTimeline = () => {
        animationState.value = "second-timeline";
        searchStartedAt = undefined;
        schedulePhase(finishActionCreationTimeline, ACTION_CREATION_TIMELINE_END_SECONDS * 1_000);
    };

    if (animationState.value === "searching" && searchStartedAt !== undefined) {
        schedulePhase(startActionCreationTimeline, durationUntilLoopBoundary(searchStartedAt, SEARCH_SPIN_DURATION_MS));
        return;
    }
    startActionCreationTimeline();
};

const showReadyVisual = () => {
    clearPhaseTimers();
    animationState.value = "ready";
    hasFinishedActionCreationTimeline = true;
};

const showVisualForStage = (stage: DevelopmentActionGenerationProgressStage) => {
    if (stage === "Getting started") {
        showIdleVisual();
        return;
    }
    if (stage === "Learning about your work") {
        showFirstTimelineVisual();
        return;
    }
    if (stage === "Finding the best opportunities") {
        showSearchVisual();
        return;
    }
    if (stage === "Creating your actions") {
        showActionCreationVisual();
        return;
    }
    showReadyVisual();
};

const stageIndex = (stage: DevelopmentActionGenerationProgressStage) => stageOrder.indexOf(stage);

const clearStageMinimumTimer = () => {
    if (stageMinimumTimer !== undefined) {
        globalThis.clearTimeout(stageMinimumTimer);
        stageMinimumTimer = undefined;
    }
};

const clearReadyTimer = () => {
    if (readyTimer !== undefined) {
        globalThis.clearTimeout(readyTimer);
        readyTimer = undefined;
    }
};

const restartSubtitleCycle = () => {
    if (subtitleTimer !== undefined) {
        globalThis.clearInterval(subtitleTimer);
        subtitleTimer = undefined;
    }
    subtitleIndex.value = 0;
    subtitleKey.value += 1;

    const messages = currentStage.value.messages;
    if (messages.length < 2) {
        return;
    }
    subtitleTimer = globalThis.setInterval(() => {
        subtitleIndex.value = (subtitleIndex.value + 1) % messages.length;
        subtitleKey.value += 1;
    }, SUBTITLE_CYCLE_DURATION_MS);
};

const advanceIfServerHasReachedNextStage = () => {
    const visualTimelineIsComplete =
        (displayedStage.value !== "Learning about your work" || hasFinishedInitialTimeline) &&
        (displayedStage.value !== "Creating your actions" || hasFinishedActionCreationTimeline);
    if (!hasMetMinimumStageDuration || !visualTimelineIsComplete || displayedStage.value === "Ready" || stageIndex(props.generationStage) <= stageIndex(displayedStage.value)) {
        return;
    }
    displayedStage.value = stageOrder[stageIndex(displayedStage.value) + 1];
};

const beginDisplayedStage = (stage: DevelopmentActionGenerationProgressStage) => {
    clearStageMinimumTimer();
    clearReadyTimer();
    hasEmittedReady = false;
    hasMetMinimumStageDuration = false;
    showVisualForStage(stage);
    restartSubtitleCycle();

    if (stage === "Ready") {
        readyTimer = globalThis.setTimeout(() => {
            if (hasEmittedReady || displayedStage.value !== "Ready" || props.generationStage !== "Ready") {
                return;
            }
            hasEmittedReady = true;
            emit("ready");
        }, READY_DISPLAY_DURATION_MS);
        return;
    }

    stageMinimumTimer = globalThis.setTimeout(() => {
        hasMetMinimumStageDuration = true;
        advanceIfServerHasReachedNextStage();
    }, MINIMUM_STAGE_DISPLAY_DURATION_MS);
};

watch(
    displayedStage,
    (stage) => {
        beginDisplayedStage(stage);
    },
    { immediate: true },
);

watch(
    displayedStage,
    (_stage, previousStage) => {
        closeVisibleStage("stage_changed", previousStage);
        viewVisibleStage();
    },
    { flush: "post" },
);

watch(
    () => props.generationStage,
    () => {
        advanceIfServerHasReachedNextStage();
    },
);

const settleInterruptedTimeline = () => {
    if (globalThis.document.visibilityState !== "visible") {
        return;
    }
    if (animationState.value === "first-timeline") {
        finishFirstTimeline();
    } else if (animationState.value === "second-timeline") {
        finishActionCreationTimeline();
    }
};

const settleRestoredPage = (event: PageTransitionEvent) => {
    if (event.persisted) {
        settleInterruptedTimeline();
    }
};

onMounted(() => {
    viewVisibleStage();
    globalThis.document.addEventListener("visibilitychange", onVisibilityChange);
    globalThis.document.addEventListener("visibilitychange", settleInterruptedTimeline);
    globalThis.addEventListener("pagehide", onPageHide);
    globalThis.addEventListener("pageshow", viewVisibleStage);
    globalThis.addEventListener("pageshow", settleRestoredPage);
});

onBeforeUnmount(() => {
    closeVisibleStage("loader_closed");
    globalThis.document.removeEventListener("visibilitychange", onVisibilityChange);
    globalThis.document.removeEventListener("visibilitychange", settleInterruptedTimeline);
    globalThis.removeEventListener("pagehide", onPageHide);
    globalThis.removeEventListener("pageshow", viewVisibleStage);
    globalThis.removeEventListener("pageshow", settleRestoredPage);
    clearTimers();
});
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .generation-progress {
        --generation-teal-strong: #30a29d;
        --generation-teal-base: #81dada;
        --generation-neutral-muted: var(--color-ink-muted);

        display: flex;
        width: 100%;
        flex-direction: column;
        align-items: center;
        padding: 3.5rem 1.5rem 4.5rem;
        text-align: center;
    }

    .generation-visual {
        width: min(100%, 28.75rem);
        height: auto;
        margin-bottom: 3.25rem;
        overflow: visible;
    }

    .generation-copy {
        display: flex;
        min-height: 4.5rem;
        max-width: 48ch;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }

    .generation-title {
        @apply font-serif font-medium;

        font-size: 26px;
        line-height: 32px;
    }

    .generation-copy-line {
        display: block;
        opacity: 0;
    }

    .generation-copy-line.is-cycling {
        animation: generation-copy-cycle 2.4s cubic-bezier(0.33, 0, 0.2, 1) both;
    }

    .generation-copy-line.is-holding {
        animation: generation-copy-enter 0.6s cubic-bezier(0.33, 0, 0.2, 1) both;
    }

    .generation-roll {
        transform: translate(0, -52px) rotate(180deg);
    }

    .generation-ball {
        opacity: 0;
    }

    /* The app's CSP blocks inline style attributes, including SVG custom properties.
       Keep the designer's contact timings: the ball crosses from 2s to 6.2s,
       then the pairs fold from 6.4s and finish before the 7.8s timeline handover. */
    .generation-mark--first {
        --mark-delay: 0.05s;
        --run-delay: 0s;
        --fold-delay: 6.4s;
        --bullet-delay: 0.15s;
        --bullet-x: 38px;
        --bullet-y: -56px;
        --squash-strong-delay: 2.6s;
        --squash-base-delay: 3.2s;
    }

    .generation-mark--second {
        --mark-delay: 0.19s;
        --run-delay: 0.22s;
        --fold-delay: 6.56s;
        --bullet-delay: 0.33s;
        --bullet-x: -66px;
        --bullet-y: -20px;
        --squash-strong-delay: 3.8s;
        --squash-base-delay: 4.4s;
    }

    .generation-mark--third {
        --mark-delay: 0.33s;
        --run-delay: 0.44s;
        --fold-delay: 6.72s;
        --bullet-delay: 0.51s;
        --bullet-x: -170px;
        --bullet-y: 16px;
        --squash-strong-delay: 5s;
        --squash-base-delay: 5.6s;
    }

    .generation-squash--strong {
        --squash-delay: var(--squash-strong-delay);
    }

    .generation-squash--base {
        --squash-delay: var(--squash-base-delay);
    }

    .generation-line--first {
        --line-delay: 1s;
    }

    .generation-line--second {
        --line-delay: 1.18s;
    }

    .generation-line--third {
        --line-delay: 1.36s;
    }

    .is-idle .generation-mark {
        animation: generation-mark-enter 0.45s ease both var(--mark-delay);
    }

    .is-idle .generation-roll {
        animation: generation-rock 3.6s linear infinite;
    }

    .is-first-timeline .generation-roll {
        animation: generation-fall 0.95s linear both var(--run-delay);
    }

    .is-first-timeline .generation-drop {
        animation: generation-land 0.44s ease-out both calc(var(--run-delay) + 0.95s);
    }

    .is-first-timeline .generation-squash {
        animation: generation-squash 0.34s ease-out both var(--squash-delay);
    }

    .is-first-timeline .generation-lift {
        animation: generation-lift 0.75s cubic-bezier(0.22, 1, 0.36, 1) both var(--fold-delay);
    }

    .is-first-timeline .generation-fold {
        animation: generation-fold 0.95s cubic-bezier(0.34, 0.9, 0.3, 1) both calc(var(--fold-delay) + 0.02s);
    }

    :is(.is-searching, .is-completing) .generation-roll {
        transform: translate(52px, 0) rotate(360deg);
    }

    :is(.is-searching, .is-completing) .generation-lift {
        transform: translate(26px, -26px);
    }

    :is(.is-searching, .is-completing) .generation-fold {
        transform: translate(-26px, -26px) rotate(180deg);
    }

    .is-searching .generation-spin {
        animation: generation-spin 1.6s cubic-bezier(0.65, 0, 0.35, 1) infinite;
    }

    .is-first-timeline .generation-ball-x {
        animation: generation-ball-x 4.2s linear both 2s;
    }

    .is-first-timeline .generation-ball-y {
        animation: generation-ball-y 0.3s cubic-bezier(0.2, 0.66, 0.4, 1) 14 alternate both 2s;
    }

    .is-first-timeline .generation-ball {
        animation:
            generation-ball-show 4.2s linear both 2s,
            generation-ball-tint 4.2s step-end both 2s;
    }

    .is-second-timeline .generation-bullet {
        animation: generation-bullet 0.85s cubic-bezier(0.5, 0, 0.2, 1) both var(--bullet-delay);
    }

    :is(.is-creating-hold, .is-ready) .generation-bullet {
        transform: translate(var(--bullet-x), var(--bullet-y)) scale(0.2308);
    }

    .generation-line {
        transform-box: fill-box;
        transform-origin: left center;
        transform: scaleX(0);
    }

    .is-second-timeline .generation-line {
        animation:
            generation-line-wipe 0.55s cubic-bezier(0.22, 1, 0.36, 1) both var(--line-delay),
            generation-skeleton 1.4s ease-in-out 2 both calc(var(--line-delay) + 0.55s);
    }

    .is-second-timeline.is-creating-actions .generation-line {
        animation:
            generation-line-wipe 0.55s cubic-bezier(0.22, 1, 0.36, 1) both var(--line-delay),
            generation-skeleton 1.4s ease-in-out infinite both calc(var(--line-delay) + 0.55s);
    }

    :is(.is-creating-hold, .is-ready) .generation-line {
        transform: scaleX(1);
    }

    .is-creating-hold .generation-line {
        animation: generation-skeleton 1.4s ease-in-out infinite;
    }

    .is-second-timeline .generation-list {
        animation: generation-list-ready 0.7s cubic-bezier(0.22, 1, 0.36, 1) both 4.9s;
    }

    :is(.is-creating-hold, .is-ready) .generation-list {
        transform: translateY(-4px);
    }

    .generation-drop,
    .generation-squash {
        transform-box: fill-box;
        transform-origin: center bottom;
    }

    @keyframes generation-copy-cycle {
        0% {
            opacity: 0;
            transform: translateY(-14px);
        }

        25%,
        75% {
            opacity: 1;
            transform: translateY(0);
        }

        100% {
            opacity: 0;
            transform: translateY(14px);
        }
    }

    @keyframes generation-copy-enter {
        from {
            opacity: 0;
            transform: translateY(-14px);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @keyframes generation-mark-enter {
        from {
            opacity: 0;
        }

        to {
            opacity: 1;
        }
    }

    @keyframes generation-rock {
        0% {
            transform: translate(-14.33px, -49.99px) rotate(148deg);
        }
        4.17% {
            transform: translate(-13.86px, -50.12px) rotate(149.09deg);
        }
        8.33% {
            transform: translate(-12.45px, -50.49px) rotate(152.29deg);
        }
        12.5% {
            transform: translate(-10.2px, -50.99px) rotate(157.37deg);
        }
        16.67% {
            transform: translate(-7.24px, -51.49px) rotate(164deg);
        }
        20.83% {
            transform: translate(-3.76px, -51.86px) rotate(171.72deg);
        }
        25% {
            transform: translate(0, -52px) rotate(180deg);
        }
        29.17% {
            transform: translate(3.76px, -51.86px) rotate(188.28deg);
        }
        33.33% {
            transform: translate(7.24px, -51.49px) rotate(196deg);
        }
        37.5% {
            transform: translate(10.2px, -50.99px) rotate(202.63deg);
        }
        41.67% {
            transform: translate(12.45px, -50.49px) rotate(207.71deg);
        }
        45.83% {
            transform: translate(13.86px, -50.12px) rotate(210.91deg);
        }
        50% {
            transform: translate(14.33px, -49.99px) rotate(212deg);
        }
        54.17% {
            transform: translate(13.86px, -50.12px) rotate(210.91deg);
        }
        58.33% {
            transform: translate(12.45px, -50.49px) rotate(207.71deg);
        }
        62.5% {
            transform: translate(10.2px, -50.99px) rotate(202.63deg);
        }
        66.67% {
            transform: translate(7.24px, -51.49px) rotate(196deg);
        }
        70.83% {
            transform: translate(3.76px, -51.86px) rotate(188.28deg);
        }
        75% {
            transform: translate(0, -52px) rotate(180deg);
        }
        79.17% {
            transform: translate(-3.76px, -51.86px) rotate(171.72deg);
        }
        83.33% {
            transform: translate(-7.24px, -51.49px) rotate(164deg);
        }
        87.5% {
            transform: translate(-10.2px, -50.99px) rotate(157.37deg);
        }
        91.67% {
            transform: translate(-12.45px, -50.49px) rotate(152.29deg);
        }
        95.83% {
            transform: translate(-13.86px, -50.12px) rotate(149.09deg);
        }
        100% {
            transform: translate(-14.33px, -49.99px) rotate(148deg);
        }
    }

    /* Preserve the prototype's sampled contact arc so the flop accelerates into impact. */
    @keyframes generation-fall {
        0% {
            transform: translate(-14.33px, -49.99px) rotate(148deg);
        }
        2.5% {
            transform: translate(-14.28px, -50px) rotate(148.13deg);
        }
        5% {
            transform: translate(-14.1px, -50.05px) rotate(148.53deg);
        }
        7.5% {
            transform: translate(-13.81px, -50.13px) rotate(149.19deg);
        }
        10% {
            transform: translate(-13.41px, -50.24px) rotate(150.12deg);
        }
        12.5% {
            transform: translate(-12.88px, -50.38px) rotate(151.31deg);
        }
        15% {
            transform: translate(-12.24px, -50.54px) rotate(152.77deg);
        }
        17.5% {
            transform: translate(-11.48px, -50.72px) rotate(154.49deg);
        }
        20% {
            transform: translate(-10.6px, -50.91px) rotate(156.48deg);
        }
        22.5% {
            transform: translate(-9.6px, -51.11px) rotate(158.73deg);
        }
        25% {
            transform: translate(-8.47px, -51.31px) rotate(161.25deg);
        }
        27.5% {
            transform: translate(-7.22px, -51.5px) rotate(164.03deg);
        }
        30% {
            transform: translate(-5.85px, -51.67px) rotate(167.08deg);
        }
        32.5% {
            transform: translate(-4.35px, -51.82px) rotate(170.39deg);
        }
        35% {
            transform: translate(-2.74px, -51.93px) rotate(173.97deg);
        }
        37.5% {
            transform: translate(-0.99px, -51.99px) rotate(177.81deg);
        }
        40% {
            transform: translate(0.87px, -51.99px) rotate(181.92deg);
        }
        42.5% {
            transform: translate(2.85px, -51.92px) rotate(186.29deg);
        }
        45% {
            transform: translate(4.95px, -51.76px) rotate(190.93deg);
        }
        47.5% {
            transform: translate(7.16px, -51.5px) rotate(195.83deg);
        }
        50% {
            transform: translate(9.48px, -51.13px) rotate(201deg);
        }
        52.5% {
            transform: translate(11.89px, -50.62px) rotate(206.43deg);
        }
        55% {
            transform: translate(14.39px, -49.97px) rotate(212.13deg);
        }
        57.5% {
            transform: translate(16.97px, -49.15px) rotate(218.09deg);
        }
        60% {
            transform: translate(19.61px, -48.16px) rotate(224.32deg);
        }
        62.5% {
            transform: translate(22.31px, -46.97px) rotate(230.81deg);
        }
        65% {
            transform: translate(25.04px, -45.57px) rotate(237.57deg);
        }
        67.5% {
            transform: translate(27.78px, -43.96px) rotate(244.59deg);
        }
        70% {
            transform: translate(30.52px, -42.1px) rotate(251.88deg);
        }
        72.5% {
            transform: translate(33.23px, -40px) rotate(259.43deg);
        }
        75% {
            transform: translate(35.88px, -37.64px) rotate(267.25deg);
        }
        77.5% {
            transform: translate(38.44px, -35.02px) rotate(275.33deg);
        }
        80% {
            transform: translate(40.89px, -32.13px) rotate(283.68deg);
        }
        82.5% {
            transform: translate(43.18px, -28.97px) rotate(292.29deg);
        }
        85% {
            transform: translate(45.3px, -25.54px) rotate(301.17deg);
        }
        87.5% {
            transform: translate(47.19px, -21.85px) rotate(310.31deg);
        }
        90% {
            transform: translate(48.82px, -17.9px) rotate(319.72deg);
        }
        92.5% {
            transform: translate(50.16px, -13.72px) rotate(329.39deg);
        }
        95% {
            transform: translate(51.16px, -9.33px) rotate(339.33deg);
        }
        97.5% {
            transform: translate(51.78px, -4.74px) rotate(349.53deg);
        }
        100% {
            transform: translate(52px, 0) rotate(360deg);
        }
    }

    @keyframes generation-land {
        0% {
            transform: scale(1);
        }

        20% {
            transform: scale(1.12, 0.85);
        }

        52% {
            transform: scale(0.96, 1.05);
        }

        78% {
            transform: scale(1.02, 0.98);
        }

        100% {
            transform: scale(1);
        }
    }

    @keyframes generation-squash {
        0% {
            transform: scale(1);
        }

        28% {
            transform: scale(1.1, 0.84);
        }

        100% {
            transform: scale(1);
        }
    }

    @keyframes generation-lift {
        0% {
            transform: translate(0, 0);
        }

        15% {
            transform: translate(2px, 3px);
        }

        100% {
            transform: translate(26px, -26px);
        }
    }

    @keyframes generation-fold {
        0% {
            transform: translate(0, 0) rotate(0deg);
        }

        55% {
            transform: translate(-10px, -54px) rotate(104deg);
        }

        100% {
            transform: translate(-26px, -26px) rotate(180deg);
        }
    }

    @keyframes generation-spin {
        from {
            transform: translate(26px, -26px) rotate(0deg) translate(-26px, 26px);
        }

        to {
            transform: translate(26px, -26px) rotate(360deg) translate(-26px, 26px);
        }
    }

    @keyframes generation-ball-x {
        from {
            transform: translateX(-26px);
        }

        to {
            transform: translateX(338px);
        }
    }

    @keyframes generation-ball-y {
        from {
            transform: translateY(-35px);
        }

        to {
            transform: translateY(-61px);
        }
    }

    @keyframes generation-ball-show {
        0% {
            opacity: 0;
        }

        2%,
        96% {
            opacity: 1;
        }

        100% {
            opacity: 0;
        }
    }

    @keyframes generation-ball-tint {
        0% {
            fill: var(--generation-neutral-muted);
        }

        14.29%,
        42.86%,
        71.43% {
            fill: var(--generation-teal-strong);
        }

        28.57%,
        57.14%,
        85.71% {
            fill: var(--generation-teal-base);
        }
    }

    @keyframes generation-bullet {
        from {
            transform: translate(0, 0) scale(1);
        }

        to {
            transform: translate(var(--bullet-x), var(--bullet-y)) scale(0.2308);
        }
    }

    @keyframes generation-line-wipe {
        from {
            transform: scaleX(0);
        }

        to {
            transform: scaleX(1);
        }
    }

    @keyframes generation-skeleton {
        0%,
        100% {
            opacity: 1;
        }

        50% {
            opacity: 0.4;
        }
    }

    @keyframes generation-list-ready {
        from {
            transform: translateY(0);
        }

        to {
            transform: translateY(-4px);
        }
    }

    @media (max-width: 860px) {
        .generation-visual {
            width: min(100%, 18.75rem);
            margin-bottom: 2.25rem;
        }

        .generation-title {
            font-size: 22px;
            line-height: 28px;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .generation-progress * {
            animation: none !important;
        }

        .is-first-timeline .generation-roll {
            transform: translate(52px, 0) rotate(360deg);
        }

        .is-first-timeline .generation-lift {
            transform: translate(26px, -26px);
        }

        .is-first-timeline .generation-fold {
            transform: translate(-26px, -26px) rotate(180deg);
        }

        .is-second-timeline .generation-bullet {
            transform: translate(var(--bullet-x), var(--bullet-y)) scale(0.2308);
        }

        .is-second-timeline .generation-list {
            transform: translateY(-4px);
        }

        .is-completing .generation-line {
            transform: scaleX(1);
        }

        .generation-copy-line {
            opacity: 1;
            transform: none;
        }
    }
}
</style>
